/**
 * Module dependencies,
 */

import { Container } from 'src/components/core/layout/container';
import { GetServerSideProps, NextPage } from 'next';
import { Lead, Title } from 'src/components/layout/homepage/styles';
import { Locale } from 'i18n-routes';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import {
  ServicesBySubcategory,
  getServicesBySubcategory
} from 'src/requests/service';

import { ServicesList } from 'src/components/layout/services/list';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { allServicesOrder } from 'src/core/constants/enums';
import { media } from 'src/styles/utils/media';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import camelCase from 'lodash/camelCase';
import styled, { createGlobalStyle } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: {
    groupedServices: Partial<ServicesBySubcategory>;
  };
};

/**
 * `GlobalStyle` styled component.
 */

const GlobalStyle = createGlobalStyle`
  body {
    background-color: var(--color-white);
  }
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  color: var(--color-gray700);
  padding-top: calc(var(--navbar-height) + 3rem);

  ${media.min.ms`
    padding-top: calc(var(--navbar-height) + 5rem);
  `}
`;

/**
 * `ServiceDetail` page.
 */

const ServiceDetail: NextPage<Props> = ({ data }) => {
  const { t } = useTranslation();
  const title = t('service:allServices.title');

  return (
    <>
      <SEO title={title} />

      <GlobalStyle />

      <TriggerNavbarTheme theme={'light'} />

      <Wrapper>
        <Container>
          <Title data-scroll={'fade-in-up'}>
            {t('service:allServices.tag')}
          </Title>

          <Lead
            as={'h1'}
            data-scroll={'fade-in-up'}
            data-scroll-animation-delay={100}
          >
            {title}
          </Lead>

          {Object.entries(data?.groupedServices).map(([key, list], index) => (
            <ServicesList
              category={list?.[0].category}
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={200 * Math.min(index + 1, 3)}
              data-scroll-offset={'20%'}
              items={list}
              key={key}
              subtitle={t(`common:service.subCategory.${camelCase(key)}`)}
            />
          ))}
        </Container>
      </Wrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps<SSGProps> = async ({
  locale
}) => {
  const services = await getServicesBySubcategory(locale as Locale);
  const groupedServices = allServicesOrder.reduce((result, category) => {
    return {
      ...result,
      // @ts-ignore
      ...(services?.[category] && { [category]: services?.[category] })
    };
  }, {});

  return {
    props: {
      data: { groupedServices },
      navbarTheme: 'light',
      ...(await serverSideTranslations(locale as Locale, ['common', 'service']))
    }
  };
};

/**
 * Export `ServiceDetail` component.
 */

export default ServiceDetail;
